import { isStandalone } from './pwa';

// App lock: an installed app keeps you signed in for weeks, so this puts the device's own unlock (Face ID, Touch ID,
// a fingerprint, Windows Hello) in front of it on launch and after it has been in the background. It uses a passkey
// created on this device purely as a local check; nothing is sent to the server. It stops someone picking up an
// unlocked phone and opening Planning; it is not encryption of what's stored on the device.

const STORAGE_KEY = 'ep:app-lock';
// Back within this long (switching apps to fetch a file, say) and it doesn't ask again.
export const LOCK_AFTER_MS = 60 * 1000;

interface Stored { credentialId: string; userId: string }

function read(): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) as Stored : null;
  } catch { return null; }
}

export function isAppLockOn(userId: string | null): boolean {
  const s = read();
  return isStandalone && !!userId && s?.userId === userId;
}

export function turnAppLockOff(): void {
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* nothing stored */ }
}

// Whether this device can do it at all: an installed app with a built-in unlock that checks it's really you.
export async function canUseAppLock(): Promise<boolean> {
  if (!isStandalone || typeof PublicKeyCredential === 'undefined') return false;
  try { return await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable(); } catch { return false; }
}

// What the device calls its unlock. The browser never says which one it will use, so this goes by the device:
// every iPhone from the X on has Face ID and a taller screen (812pt or more); the SE and 8 have Touch ID. iPads
// come both ways at the same sizes, and iPadOS reports itself as a Mac with a touch screen.
export function unlockName(): string {
  const ua = navigator.userAgent;
  const touchMac = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  if (/iPhone|iPod/.test(ua)) return Math.max(screen.width, screen.height) >= 812 ? 'Face ID' : 'Touch ID';
  if (/iPad/.test(ua) || touchMac) return 'Face ID or Touch ID';
  if (/Android/.test(ua)) return 'your fingerprint or face';
  if (/Macintosh/.test(ua)) return 'Touch ID';
  if (/Windows/.test(ua)) return 'Windows Hello';
  return 'your screen lock';
}

function toB64Url(buf: ArrayBuffer): string {
  return btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromB64Url(s: string): Uint8Array<ArrayBuffer> {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

// On the app's own address (planning.elw-consulting.co.uk and friends) the passkey belongs to elw-consulting.co.uk,
// which is what the device shows in its passwords list; elsewhere (the CloudFront address) it is the page's own host.
// create() and get() must agree, so both use this.
function rpId(): string | undefined {
  return location.hostname.endsWith('.elw-consulting.co.uk') ? 'elw-consulting.co.uk' : undefined;
}

function challenge(): Uint8Array<ArrayBuffer> {
  return crypto.getRandomValues(new Uint8Array(32));
}

// Turns it on: creates a passkey on this device, which also asks for the unlock once.
export async function turnAppLockOn(userId: string, email: string): Promise<void> {
  const cred = await navigator.credentials.create({
    publicKey: {
      rp: { name: 'Elevations Planning', ...(rpId() && { id: rpId() }) },
      user: { id: new TextEncoder().encode(userId), name: email, displayName: email },
      challenge: challenge(),
      pubKeyCredParams: [{ type: 'public-key', alg: -7 }, { type: 'public-key', alg: -257 }],
      authenticatorSelection: { authenticatorAttachment: 'platform', userVerification: 'required', residentKey: 'discouraged' },
      timeout: 60_000,
    },
  }) as PublicKeyCredential | null;
  if (!cred) throw new Error('The device did not set up the lock.');
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ credentialId: toB64Url(cred.rawId), userId } satisfies Stored));
}

// Asks for the device's unlock. Resolves true only when the device confirms it checked it was really you (the
// user-verified flag in the response); false or a throw otherwise. NotAllowedError covers both "cancelled" and
// "the browser wants a tap first".
export async function unlockApp(signal?: AbortSignal): Promise<boolean> {
  const s = read();
  if (!s) return true;
  const cred = await navigator.credentials.get({
    publicKey: {
      challenge: challenge(),
      ...(rpId() && { rpId: rpId() }),
      allowCredentials: [{ type: 'public-key', id: fromB64Url(s.credentialId), transports: ['internal'] }],
      userVerification: 'required',
      timeout: 60_000,
    },
    signal,
  }) as PublicKeyCredential | null;
  if (!cred || toB64Url(cred.rawId) !== s.credentialId) return false;
  const authData = new Uint8Array((cred.response as AuthenticatorAssertionResponse).authenticatorData);
  return (authData[32] & 0x04) !== 0;
}
